import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Edit from "../../../assets/EditInWhite.svg";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import Paginator, {
  PaginatorForRDT,
} from "../../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../../components/CommonComponents/Table/Table";
import { useDebounce } from "../../../hooks/common";
import { TForm, useFormsCount, useGetForms } from "../../../hooks/forms.hooks";
import { formatDate } from "../../../utils/date";
import EditButton, { ViewButton } from "../../../components/CommonComponents/EditComponent/EditComponent";

export default function FormConfigurations() {
  const limit = 10;
  const [view, setView] = useState<"table" | "card">("table");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "",
    sortOrder: 1,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const search = useDebounce(searchQuery);

  const { isLoading: isLoading1, data: count } = useFormsCount({ search });
  const { isLoading: isLoading2, data } = useGetForms({
    search,
    page: page,
    limit: limit,
    sort: sortKey,
    sortVal: sortOrder,
  });

  const columns = useMemo(
    (): TableColumn<TForm>[] => [
      {
        name: "Sr. No.",
        // selector: (_, i) => (page - 1) * limit + (i ?? 0) + 1,
        selector: (_, i) => {
          const serialNumber = (page - 1) * limit + (i ?? 0) + 1;
          return serialNumber.toString().padStart(3, '0');
        },
        grow: 0.5,
        center: true,
      },
      {
        name: "Form Name",
        cell: (d) => <span className="text-center">{d.name || "-"}</span>,
        center: true,
        sortable: true,
        sortField: "name",
        grow: 3,
      },
      {
        name: "Name of Service",
        cell: (d) => (
          <span className="text-center">{d.serviceName || "-"}</span>
        ),
        center: true,
        sortable: true,
        sortField: "serviceName",
        width: "245px",
      },
      {
        name: "Form Type",
        selector: (d) => d.type,
        center: true,
        sortable: true,
        sortField: "type",
        width: "230px"
      },
      {
        name: "Last Published Date",
        selector: (d) => formatDate(d.updatedAt),
        center: true,
        sortable: true,
        sortField: "updatedAt",
        width: "225px",
        sortFunction: (a: any, b: any) => {
          const dateA = new Date(a.updated_date);
          const dateB = new Date(b.updated_date);
          return dateA.getTime() - dateB.getTime();
        }

      },
      {
        name: "Status",
        width: '115px',
        center: true,
        sortable: true,
        sortField: "active",
        cell: (d) => (
          <div
            className={`${d.active ? "activeButtonCss" : "inactiveButtonCss"
              // } inline-block py-1 px-4 md:px-6 rounded-full shrink-0 text-white text-xs`}
              } flex justify-center items-center`}
          >
            {d?.active ? "Active" : "Inactive"}
          </div>
        ),
      },
      {
        name: 'Created By',
        selector: (row: any) => row.createdBy,
        cell: (row: any) => <span className="text-center">{row.createdBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified By',
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => <span className="text-center">{row.modifiedBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        grow: 0.5,
        cell: (d) => (
          // <img
          //   src={Edit}
          //   alt="Edit"
          //   onClick={() => navigate(`/edit-form-configuration/${d.id}`)}
          //   className="globalEditCss"
          // />
          <div className="flex space-x-2">
            <EditButton
              onClick={() => navigate(`/edit-form-configuration/${d.id}`)}
            />
            <ViewButton
              disabled={true}
              onClick={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          </div>
        ),
      },
    ],
    [navigate, page, limit]
  );

  return (
    <div>
      <div className="p-4 md:p-8 overflow-auto">
        <div className="flex flex-wrap md:flex-nowrap justify-between gap-2 mb-2">
          <h4 className="text-2xl font-bold">Form Configuration</h4>
          <div className="space-x-2 md:space-x-4">
            {/* <button
              className="py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg"
              onClick={() => navigate("/add-step-configuration")}
            >
              Step Configuration
            </button> */}
            <button
              className="py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg"
              onClick={() => navigate("/add-form-configuration")}
            >
              Create
            </button>
          </div>
        </div>
        <div className="flex-wrap md:flex-nowrap flex-col-reverse md:flex-row flex justify-between md:items-center gap-4">
          <div className="shrink-0">
            <button className="p-2 lg:p-4 font-semibold text-base md:text-lg rounded-t-2xl bg-white border-b-2 border-primary">
              Form Configuration
            </button>
            <button
              onClick={() => navigate("/step-configurations")}
              className="p-2 lg:p-4 font-semibold text-base md:text-lg rounded-t-2xl bg-gray-200 border-b-2 border-gray-200"
            >
              Step Configuration
            </button>
          </div>

          <div className="flex items-center space-x-4 grow md:grow-0">
            <button
              className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
              onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
            >
              {view === "table" ? (
                <i className="fa fa-th text-primary m-0" />
              ) : (
                <i className="fa fa-list-ul text-primary m-0" />
              )}
            </button>
            <div className="flex rounded-xl border border-[#968f8f] bg-white items-stretch overflow-hidden grow md:grow-0">
              <input
                type="text"
                placeholder="Search..."
                className="bg-none px-3"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <div className="p-3 shadow-md flex justify-center items-center">
                <i className="fa fa-search text-primary m-0" />
              </div>
            </div>
          </div>
        </div>
        {view === "table" ? (
          <BoxComponent>
            <DataTable
              pagination
              paginationServer
              className="mt-4 rounded-0"
              columns={columns}
              data={data?.data || []}
              onChangePage={(p) => setPage(p)}
              customStyles={tableCustomStyles}
              sortIcon={<SortIcon />}
              paginationTotalRows={count?.data || 0}
              progressPending={isLoading1 || isLoading2}
              paginationComponent={PaginatorForRDT}
              onSort={(col, dir) => {
                setSort({
                  sortKey: col.sortField || "",
                  sortOrder: dir === "asc" ? 1 : -1,
                });
              }}
            />
          </BoxComponent>
        ) : (
          <>
            <BoxComponent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {(data?.data || []).map((e) => (
                  <div
                    className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300"
                    key={e.id}
                  >
                    <div className="flex justify-between items-center space-x-4 mb-4">
                      <div
                        className={`${e.active ? "activeButtonCss" : "inactiveButtonCss"
                          // } inline-block py-1 px-4 rounded-full text-white text-xs`}
                          } flex justify-center items-center`}
                      >
                        {e?.active ? "Active" : "Inactive"}
                      </div>
                      {/* <img
                        src={Edit}
                        alt="Edit"
                        onClick={() => navigate(`/edit-step-configuration/${e.id}`)}
                        className="globalEditCss"
                      /> */}
                      <div className="flex space-x-2">
                        <EditButton
                          onClick={() => navigate(`/edit-form-configuration/${e.id}`)}
                        />
                        <ViewButton
                          disabled={true}
                          onClick={function (): void {
                            throw new Error("Function not implemented.");
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-2 flex text-[12px]">
                      <p className="font-bold text-base w-1/2">Form Name</p>
                      <p className="text-base mx-2">:</p>
                      <p className="text-base w-2/3">{e.name}</p>
                    </div>
                    <div className="mb-2 flex text-[12px]">
                      <p className="font-bold text-base w-1/2">
                        Last Published Date
                      </p>
                      <p className="text-base mx-2">:</p>
                      <p className="text-base w-2/3">
                        {formatDate(e.updatedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Paginator
                limit={limit}
                page={page}
                setPage={setPage}
                count={count?.data || 0}
              />
            </BoxComponent>
          </>
        )}
      </div>
    </div>
  );
}
